import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { ContractCeRateRequest, ContractCeRateResponse, ContractCeRatesClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { CeRatesDataModel } from 'src/views/contract/vesselContract/Import/DataTypes';

export interface ContractCeRate extends ContractCeRateResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    contractCeRateList: ContractCeRate[];
}

const initialState: State = {
    isLoading: true,
    error: false,
    contractCeRateList: [],
};

const contractCeRateSlice = createSlice({
    name: 'contractCeRates',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getContractCeRateListSuccess: (state, action: PayloadAction<ContractCeRate[]>) => {
            state.contractCeRateList = action.payload;
            state.isLoading = false;
        },
        addNewContractCeRateSuccess: (state, action: PayloadAction<ContractCeRate>) => {
            const newItem = action.payload;
            state.contractCeRateList = [newItem, ...state.contractCeRateList];
            toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            state.isLoading = false;
        },
        updateContractCeRateSuccess: (state, action: PayloadAction<ContractCeRate>) => {
            const updatedItem = action.payload;
            const index = state.contractCeRateList.findIndex(item => item.contractCeRateId === updatedItem.contractCeRateId);
            state.contractCeRateList = [...state.contractCeRateList.slice(0, index), updatedItem, ...state.contractCeRateList.slice(index + 1)];
            toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
            state.isLoading = false;
        },
        deleteContractCeRateSuccess: (state, action: PayloadAction<number>) => {
            const contractCeRateId = action.payload;
            state.contractCeRateList = state.contractCeRateList.filter(item => item.contractCeRateId !== contractCeRateId);
            toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default contractCeRateSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
} = contractCeRateSlice.actions;

// for useSelector
export const selectContractCeRates = (state: RootState) => state.contractCeRates as State;

// ----------------------------------------------------------------------

export function getContractCeRateList(vesselContractId: number) {
    return async (dispatch, getState) => {
        dispatch(contractCeRateSlice.actions.startLoading());
        try {
            const client = new ContractCeRatesClient(undefined, axios);
            const response = await client.getByVesselContractId(vesselContractId);
            dispatch(contractCeRateSlice.actions.getContractCeRateListSuccess(response));
            return response;
        } catch (error) {
            dispatch(contractCeRateSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createContractCeRate(contractCeRate: ContractCeRate) {
    return async (dispatch, getState) => {
        dispatch(contractCeRateSlice.actions.startLoading());
        try {
            const { contractCeRateId, ...payload } = contractCeRate;
            const request = payload as ContractCeRateRequest;
            const client = new ContractCeRatesClient(undefined, axios);
            const response = await client.create(request);
            dispatch(contractCeRateSlice.actions.addNewContractCeRateSuccess(response));
        } catch (error) {
            dispatch(contractCeRateSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createContractCeRateBatch(vesselContractId: number, CostsData: CeRatesDataModel[],
    toastId: ReactText, currentProgress: number, totalProgress: number) {
    return async (dispatch, getState) => {
        try {
            const client = new ContractCeRatesClient(undefined, axios);
            for (const d of CostsData) {
                const request = {
                    vesselContractId: vesselContractId,
                    minPoints: d.MinPoints,
                    maxPoints: d.MaxPoints,
                    rate: d.Rate
                } as ContractCeRateRequest;
                await client.create(request);
                currentProgress += 1;
                toast.update(toastId, { render: `${stringResources.IMPORT.COSTS_IMPORTING} (${currentProgress}/${totalProgress})`, progress: currentProgress / totalProgress });
            }
        } catch (error) {
            handleError(error);
        }
    };
}

export function updateContractCeRate(contractCeRate: ContractCeRate) {
    return async (dispatch, getState) => {
        dispatch(contractCeRateSlice.actions.startLoading());
        try {
            const { contractCeRateId, ...payload } = contractCeRate;
            const request = payload as ContractCeRateRequest;
            const client = new ContractCeRatesClient(undefined, axios);
            const response = await client.edit(contractCeRateId, request);
            dispatch(contractCeRateSlice.actions.updateContractCeRateSuccess(response));
        } catch (error) {
            dispatch(contractCeRateSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteContractCeRate(contractCeRateId: number) {
    return async (dispatch, getState) => {
        dispatch(contractCeRateSlice.actions.startLoading());
        try {
            const client = new ContractCeRatesClient(undefined, axios);
            await client.delete(contractCeRateId);
            dispatch(contractCeRateSlice.actions.deleteContractCeRateSuccess(contractCeRateId));
        } catch (error) {
            dispatch(contractCeRateSlice.actions.hasError(true));
            handleError(error);
        }
    };
}
